import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, ButtonBase, CircularProgress, IconButton, Modal } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

import { qrcodeAPIService } from '~/api/qrcode';
import SearchIcon from '~/assets/icons/svgs/search.svg?react';
import { Button } from '~/components/ui/button';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { folderKeys, qrCodeKeys } from '~/constants/queryKeys';
import useQrCodeFolders from '~/hooks/useQrCodeFolders';
import { COLORS } from '~/styles/colors';
import { QrCodeStatus, UpdateQrCodeDataPayload } from '~/types/qrCode';
import { FormatLocales } from '~/utils/formatLocales';

type SelectedQR = {
  id: number;
  type: 'DYNAMIC' | 'STATIC';
  status: QrCodeStatus;
  folderId: null | number;
};

type SaveToAnotherFolderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  openCreateModal: () => void;
  selectedQRs: SelectedQR[];
  setSelectedQRs: Dispatch<SetStateAction<SelectedQR[]>>;
};

export default function SaveToAnotherFolderModal({
  isOpen,
  onClose,
  openCreateModal,
  selectedQRs,
  setSelectedQRs,
}: SaveToAnotherFolderModalProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [searchValue, setSearchValue] = useState();
  const [selectedFolderId, setSelectedFolderId] = useState<null | number>(null);

  const styles = useStyles();

  const { data: folders } = useQrCodeFolders();
  const { mutateAsync: updateQrCodeMutateAsync, isPending: isPendingUpdateUpdateQrCode } = useMutation({
    mutationFn: ({ payload, qrCodeId }: { payload: UpdateQrCodeDataPayload; qrCodeId: number }) =>
      qrcodeAPIService.updateQrCode(payload, qrCodeId),
  });

  const isSaveDisabled = !folders.length || selectedFolderId === null;

  const displayedFolders = useMemo(() => {
    if (!folders) {
      return [];
    }

    if (!searchValue) {
      return folders;
    }

    return folders.filter(({ name }) => name.toLowerCase().includes(searchValue.toLowerCase()));
  }, [folders, searchValue]);

  const handleUpdateFolder = async () => {
    if (!isSaveDisabled) {
      const qrIdsToUpdate = selectedQRs
        .filter(({ folderId }) => folderId === null || folderId !== selectedFolderId)
        .map(({ id }) => id);

      if (qrIdsToUpdate.length) {
        await Promise.all(
          qrIdsToUpdate.map((id) =>
            updateQrCodeMutateAsync({ payload: { folder_id: selectedFolderId }, qrCodeId: id }),
          ),
        );

        setSelectedQRs([]);

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: qrCodeKeys.myQrsData }),
          queryClient.invalidateQueries({ queryKey: folderKeys.allFolders }),
        ]);
      }

      onClose();
    }
  };

  return (
    <Modal
      open={isOpen}
      sx={styles.modal}
      slotProps={{
        backdrop: {
          sx: styles.backdrop,
        },
      }}
    >
      <Box sx={styles.container}>
        <IconButton onClick={onClose} sx={styles.closeIconWrapper}>
          <CloseIcon sx={styles.closeIcon} />
        </IconButton>

        <Text variant="h2" sx={styles.title}>
          {t('folders:selectOrCreateFolder')}
        </Text>

        <Text variant="input15" sx={{ textAlign: 'center', color: COLORS.grey400 }}>
          {t('folders:selectFolderDescription')}
        </Text>

        <TextInput
          icon={<SearchIcon />}
          value={searchValue}
          placeholder={t('folders:searchFolder')}
          sx={{ marginBottom: '32px', marginTop: '24px' }}
          onChange={(event) => {
            setSearchValue(event.target.value);
          }}
        />

        {folders?.length && displayedFolders.length ? (
          <Box sx={styles.foldersWrapper}>
            {displayedFolders.map(({ id, name, createdAtRaw, qrCodesCount }) => {
              return (
                <ButtonBase
                  sx={styles.folder}
                  onClick={() => setSelectedFolderId(id)}
                  disableRipple
                  key={id}
                  className={id === selectedFolderId ? 'selected' : undefined}
                >
                  <Text variant="t16Medium" sx={{ textAlign: 'left' }}>
                    {name}
                  </Text>
                  <Box sx={styles.folderBottom}>
                    <Text variant="inputVerySmall" sx={{ color: COLORS.grey400 }}>
                      {format(createdAtRaw, 'MMMM d, yyyy', { locale: FormatLocales[i18n.language] })}
                    </Text>
                    <Text
                      variant="input15"
                      sx={{ padding: '2px 8px', borderRadius: '50%', backgroundColor: COLORS.grey500 }}
                    >
                      {qrCodesCount}
                    </Text>
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
        ) : (
          <Text variant="input15" sx={{ textAlign: 'center', color: COLORS.grey400 }}>
            {t(folders.length ? 'folders:noResults' : 'folders:emptyFolders')}
          </Text>
        )}

        <Box sx={styles.bottomActions}>
          <Button sx={styles.createFolder} onClick={openCreateModal} disableRipple size="small">
            + {t('builder:nameAndFolder.createFolder')}
          </Button>
          <Button
            onClick={handleUpdateFolder}
            disabled={isSaveDisabled}
            disableRipple
            endIcon={isPendingUpdateUpdateQrCode ? <CircularProgress size={20} color="inherit" /> : undefined}
            size="small"
            sx={styles.save}
            variant={isSaveDisabled ? 'contained' : 'outlined'}
          >
            {t('buttons:save')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

function useStyles() {
  return {
    backdrop: css`
      background-color: #c9c8c84d;
      backdrop-filter: blur(3px);
    `,
    modal: css`
      display: flex;
      align-items: center;
      justify-content: center;
    `,
    closeIconWrapper: css`
      margin-left: auto;
    `,
    container: css`
      width: 100%;
      max-width: 475px;
      max-height: 80dvh;
      padding: 20px 32px 40px;
      border-radius: 20px;
      background-color: ${COLORS.white};
      display: flex;
      flex-direction: column;
    `,
    closeIcon: css`
      color: ${COLORS.textMain};
    `,
    title: css`
      cursor: initial;
      text-align: center;
      margin-bottom: 8px;
    `,
    createFolder: css`
      height: 40px;
      padding: 4px 20px !important;
    `,
    save: css`
      height: 40px;
      padding: 4px 20px !important;
      min-width: 170px !important;

      &:not(:disabled) {
        color: ${COLORS.primary};
        border-color: ${COLORS.primary};

        &:hover {
          color: ${COLORS.primary};
          border-color: ${COLORS.primary};
        }
      }
    `,
    bottomActions: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 32px;
    `,
    foldersWrapper: css`
      display: flex;
      flex-direction: column;
      gap: 12px;
      //max-height: calc(
      //  100dvh - 614px
      //); // 614px - the sum of 356 + 258, 258 - sum of top and bottom distances to modal content, 356 - the sum of all content, paddings and margins except content for folders
      overflow-y: auto;
    `,
    folder: css`
      border-radius: 16px;
      padding: 16px 20px;
      border: 1px solid ${COLORS.grey200};
      display: block;
      min-height: fit-content;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      word-wrap: break-word;
      overflow-wrap: break-word;
      white-space: normal;

      &.selected {
        border-width: 2px;
        border-color: ${COLORS.primary};
      }
    `,
    folderBottom: css`
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
      margin-top: 2px;
    `,
  };
}
